import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { request } from "../../../utils/api";
import { toast, ToastContainer } from "react-toastify";
import { makeError } from "../../../utils";
import * as Yup from "yup";
import NotFoundTitle from "../utilComponent/NotFoundTitle";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaImage } from "react-icons/fa";
import { AuthContext } from "../../../auth/AuthContext";
import Loading from "../utilComponent/Loading";

const AddHero = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataFound, setDataFound] = useState(true);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    user_id: user?.id || "",
    title: "",
    description: "",
    timeline: "",
    status: "active",
    image: null,
  });

  const schema = Yup.object().shape({
    title: Yup.string().required("Title is required"),
    timeline: Yup.string().required("Timeline is required"),
     status: Yup.string()
    .oneOf(["active", "inactive"])
    .required("Status is required"),
    description: Yup.string().test(
      "not-empty",
      "Description is required",
      (value) =>
        value && value.replace(/<(.|\n)*?>/g, "").trim().length > 0
    ),
  });

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    setFormData(updated);

    try {
      await schema.validateAt(name, updated);
      setErrors((prev) => ({ ...prev, [name]: "" }));
    } catch (err) {
      setErrors((prev) => ({ ...prev, [name]: err.message }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setFormData((prev) => ({ ...prev, image: file }));
    setImagePreview(URL.createObjectURL(file));
  };

  const getHeroById = async () => {
    try {
      setLoading(true);
      const res = await request({
        url: `heroes/${id}`,
        method: "GET",
      });

      if (res?.data) {
        setFormData({
          user_id: res.data.user_id || user?.id || "",
          title: res.data.title || "",
          description: res.data.description || "",
          timeline: res.data.timeline || "",
          status: res.data.status || "active", 
          image: res.image || null,
        });

        setImagePreview(res.image || null);
        setDataFound(true);
      } else {
        setDataFound(false);
      }
    } catch (error) {
      setDataFound(false);
      makeError(error, toast);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) getHeroById();
  }, [id]);

  const submit = async () => {
    try {
      await schema.validate(formData, { abortEarly: false });
      setErrors({});
      id ? updateService() : addService();
    } catch (err) {
      const newErrors = {};
      err.inner.forEach((e) => {
        newErrors[e.path] = e.message;
      });
      setErrors(newErrors);
    }
  };

  const getPayload = () => {
    const payload = new FormData();
    payload.append("title", formData.title);
    payload.append("description", formData.description);
    payload.append("timeline", formData.timeline);
    payload.append("status", formData.status); 
    if (formData.image) payload.append("image", formData.image);
    return payload;
  };

  const addService = async () => {
    try {
      setPostLoading(true);
      await request({
        url: "heroes",
        method: "POST",
        data: getPayload(),
      });
      toast.success("Service added successfully");
      navigate("/hero-list");
    } catch (error) {
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const updateService = async () => {
    try {
      setPostLoading(true);
      await request({
        url: `heroes/${id}`,
        method: "POST",
        data: getPayload(),
      });
      toast.success("Service updated successfully");
      navigate("/hero-list");
    } catch (error) {
      makeError(error, toast);
    } finally {
      setPostLoading(false);
    }
  };

  const quillModules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline"],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
      ["clean"],
    ],
  };

  return (
    <>
      <ToastContainer />

      <div className="container-fluid px-4 py-3">
        <ol className="breadcrumb bg-transparent mb-3">
          <li className="breadcrumb-item">
            <Link to="/">Home</Link>
          </li>
          <li className="breadcrumb-item">Hero</li>
          <li className="breadcrumb-item active">
            {id ? "Update" : "Add"} Hero
          </li>
        </ol>

        {!dataFound ? (
          <NotFoundTitle message="Hero not found" />
        ) : (
          <div className="card">
            <div className="card-header">
              <h6>{id ? "Update Hero" : "Add Hero"}</h6>
            </div>

            <div className="card-body">
              {loading && <Loading/>}
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label fw-semibold">Title *</label>
                  <input
                    type="text"
                    name="title"
                    className="form-control"
                    value={formData.title}
                    onChange={handleInputChange}
                  />
                  {errors.title && (
                    <div className="text-danger">{errors.title}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Timeline *
                  </label>
                  <input
                    type="text"
                    name="timeline"
                    className="form-control"
                    placeholder="e.g. 3–5 days"
                    value={formData.timeline}
                    onChange={handleInputChange}
                  />
                  {errors.timeline && (
                    <div className="text-danger">{errors.timeline}</div>
                  )}
                </div>

                <div className="col-md-6">
                  <label className="form-label fw-semibold">
                    Status *
                  </label>
                  <select
                    name="status"
                    className="form-select"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                  {errors.status && (
                    <div className="text-danger">{errors.status}</div>
                  )}
                </div>

                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Description *
                  </label>
                  <ReactQuill
                    theme="snow"
                    value={formData.description}
                    onChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: value,
                      }))
                    }
                    modules={quillModules}
                  />
                  {errors.description && (
                    <div className="text-danger mt-1">
                      {errors.description}
                    </div>
                  )}
                </div>

                {/* IMAGE */}
                <div className="col-12">
                  <label className="form-label fw-semibold">
                    Image
                  </label>
                  <div className="input-group">
                    <span className="input-group-text">
                      <FaImage />
                    </span>
                    <input
                      type="file"
                      className="form-control"
                      accept="image/*"
                      onChange={handleFileChange}
                    />
                  </div>

                  {imagePreview && (
                    <img
                      src={imagePreview}
                      alt="Preview"
                      className="mt-3 rounded border"
                      style={{
                        width: "120px",
                        height: "120px",
                        objectFit: "cover",
                      }}
                    />
                  )}
                </div>
              </div>
            </div>

            <div className="card-footer text-end">
              <button
                className="btn btn-secondary"
                onClick={() => navigate("/services-list")}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary ms-3"
                onClick={submit}
                disabled={postLoading}
              >
                {postLoading
                  ? "Saving..."
                  : id
                  ? "Update"
                  : "Add"}
              </button>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AddHero;